<template>
  <div class="ZpAuth">
    <div class="ZpAuth__panel">

      <header class="ZpAuth__brand">
        <svg class="ZpAuth__logo" viewBox="0 0 64 64" fill="none"
             role="img" aria-label="ZetaPilot">
          <g stroke="currentColor" stroke-width="4" stroke-linejoin="round">
            <rect x="13" y="8" width="38" height="48" rx="4" fill="none" />
            <rect x="20" y="17" width="24" height="5" rx="1"
                  fill="currentColor" stroke="none" />
            <rect x="20" y="26" width="24" height="5" rx="1"
                  fill="currentColor" stroke="none" />
            <rect x="20" y="35" width="24" height="5" rx="1"
                  fill="currentColor" stroke="none" />
            <rect x="20" y="44" width="24" height="5" rx="1"
                  fill="currentColor" stroke="none" />
          </g>
        </svg>

        <div class="ZpAuth__wordmark">
          <span class="ZpAuth__brand-name">ZetaPilot</span>
          <span class="ZpAuth__brand-tag">{{ $t('brandClassifier') }}</span>
        </div>
      </header>

      <h1 class="ZpAuth__title" v-if="screen === 'verification'">
        {{ $t('twoStepVerification') }}
      </h1>
      <h1 class="ZpAuth__title" v-else-if="screen === 'recovery'">
        {{ $t('accountRecovery') }}
      </h1>
      <h1 class="ZpAuth__title" v-else>
        {{ $t('signInTitle') }}
      </h1>

      <p class="ZpAuth__subtitle" v-if="screen === 'sign-in'">
        {{ $t('signInSubtitle') }}
      </p>

      <v-form
        @submit.prevent
        ref="signInForm"
        lazy-validation
        v-model="signInFormValid"
        class="ZpAuth__form"
      >

        <v-alert
          v-if="signInError"
          dense
          text
          type="error"
          class="ZpAuth__error"
        >
          {{ signInError }}
        </v-alert>

        <div v-if="screen === 'verification'">

          <p class="ZpAuth__hint" v-if="verificationMethod === 'totp'">
            {{ $t('totpHint') }}
          </p>

          <p class="ZpAuth__hint"
             v-else-if="isPortal && verificationMethod === 'email'">
            {{ $t('emailOtpHint') }}
          </p>

          <v-otp-input
            v-model="verificationCode"
            length="6"
            @finish="verify()"
          />

          <div class="ZpAuth__row">
            <a class="ZpAuth__link" @click="signOut()">
              {{ $t('Return to login') }}
            </a>
            <a
              class="ZpAuth__link"
              v-if="verificationMethod === 'totp'
                && authMethods.totp
                && authMethods.totp.allow_recovery"
              @click="screen = 'recovery'"
            >
              {{ $t('Use recovery code') }}
            </a>
          </div>

          <v-btn
            v-if="isPortal && verificationMethod === 'email'"
            depressed
            block
            :disabled="verificationEmailSending"
            color="primary"
            class="ZpAuth__btn"
            @click="resendEmailVerification()"
          >
            {{
              verificationEmailSending
                ? $t('Email sending...')
                : $t('Resend code to email')
            }}
          </v-btn>
        </div>

        <div v-else-if="screen === 'recovery'">
          <p class="ZpAuth__hint">{{ $t('recoveryHint') }}</p>

          <v-text-field
            v-model="recoveryCode"
            outlined
            dense
            :label="$t('Recovery code')"
            :rules="[v => !!v || $t('recoveryCode_required')]"
            required
            class="ZpAuth__field"
            @keyup.enter.native="signIn"
          />

          <v-btn
            depressed
            block
            color="primary"
            class="ZpAuth__btn"
            @click="recovery()"
          >
            {{ $t('continue') }}
          </v-btn>

          <div class="ZpAuth__row ZpAuth__row--center">
            <a class="ZpAuth__link" @click="screen = 'verification'">
              {{ $t('Return to verification') }}
            </a>
          </div>
        </div>

        <div v-else>

          <div v-if="loginWithPassword">
            <v-text-field
              v-model="username"
              outlined
              dense
              :label="$t('username')"
              :rules="[v => !!v || $t('username_required')]"
              required
              :disabled="signInProcess"
              autocomplete="username"
              id="auth-username"
              data-testid="auth-username"
              class="ZpAuth__field"
            />

            <v-text-field
              v-model="password"
              outlined
              dense
              :label="$t('password')"
              :rules="[v => !!v || $t('password_required')]"
              type="password"
              required
              :disabled="signInProcess"
              autocomplete="current-password"
              @keyup.enter.native="signIn"
              id="auth-password"
              data-testid="auth-password"
              class="ZpAuth__field"
            />

            <v-btn
              depressed
              block
              color="primary"
              class="ZpAuth__btn"
              :disabled="signInProcess"
              :loading="signInProcess"
              @click="signIn"
              data-testid="auth-signin"
            >
              {{ $t('signIn') }}
            </v-btn>
          </div>

          <div v-else-if="isPortal">
            <v-text-field
              v-model="email"
              outlined
              dense
              :label="$t('Email')"
              :rules="[v => !!v || $t('email_required')]"
              type="email"
              required
              :disabled="signInProcess"
              autocomplete="email"
              @keyup.enter.native="signInWithEmail"
              data-testid="auth-password"
              class="ZpAuth__field"
            />

            <v-btn
              depressed
              block
              color="primary"
              class="ZpAuth__btn"
              :disabled="signInProcess"
              :loading="signInProcess"
              @click="signInWithEmail"
              data-testid="auth-signin-with-eamil"
            >
              <ZpIcon name="mail" :size="18" class="mr-2" color="#fff" />
              {{ $t('Continue with Email') }}
            </v-btn>
          </div>

          <div
            v-if="(loginWithPassword || isPortal) && oidcProviders.length > 0"
            class="ZpAuth__divider"
          >
            <span>{{ $t('or') }}</span>
          </div>

          <v-btn
            v-for="provider in oidcProviders"
            :key="provider.id"
            outlined
            block
            class="ZpAuth__btn ZpAuth__btn--secondary"
            :color="provider.color || ''"
            @click="oidcSignIn(provider.id)"
          >
            <v-icon left v-if="provider.icon">mdi-{{ provider.icon }}</v-icon>
            {{ provider.name }}
          </v-btn>
        </div>
      </v-form>

      <footer class="ZpAuth__footer">
        ZetaPanel · ZetaPilot
      </footer>
    </div>
  </div>
</template>

<style lang="scss">
.ZpAuth {
  min-height: 100dvh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--zp-space-lg);
  background:
    radial-gradient(
      circle at 80% -10%,
      color-mix(in oklch, var(--zp-brand) 20%, transparent) 0%,
      transparent 45%
    ),
    radial-gradient(
      circle at -10% 110%,
      color-mix(in oklch, var(--zp-brand) 14%, transparent) 0%,
      transparent 50%
    ),
    var(--zp-bg-page);
}

.ZpAuth__panel {
  width: 100%;
  max-width: 380px;
  background: var(--zp-bg-surface);
  border: var(--zp-border-hair) solid var(--zp-border-hairline);
  border-radius: var(--zp-radius-lg);
  padding: var(--zp-space-2xl) var(--zp-space-xl);
  box-shadow:
    0 1px 2px rgba(15, 18, 35, 0.04),
    0 12px 28px -8px rgba(15, 18, 35, 0.08);
}

.ZpAuth__brand {
  display: flex;
  align-items: center;
  gap: var(--zp-space-sm);
  margin-bottom: var(--zp-space-xl);
}

.ZpAuth__logo {
  width: 36px;
  height: 36px;
  color: var(--zp-brand);
  flex: 0 0 auto;
}

.ZpAuth__wordmark {
  display: flex;
  flex-direction: column;
  line-height: 1.1;
}

.ZpAuth__brand-name {
  font-family: var(--zp-font-display);
  font-size: var(--zp-text-lg);
  font-weight: 600;
  letter-spacing: var(--zp-tracking-display);
  color: var(--zp-text-primary);
}

.ZpAuth__brand-tag {
  font-family: var(--zp-font-body);
  font-size: var(--zp-text-2xs);
  font-weight: 600;
  letter-spacing: var(--zp-tracking-overline);
  text-transform: uppercase;
  color: var(--zp-text-tertiary);
  margin-top: 2px;
}

.ZpAuth__title {
  font-family: var(--zp-font-display);
  font-size: var(--zp-text-xl);
  font-weight: 600;
  letter-spacing: var(--zp-tracking-display);
  color: var(--zp-text-primary);
  margin: 0 0 var(--zp-space-2xs);
  line-height: var(--zp-leading-tight);
}

.ZpAuth__subtitle {
  font-size: var(--zp-text-sm);
  color: var(--zp-text-secondary);
  margin: 0 0 var(--zp-space-lg);
  line-height: var(--zp-leading-normal);
}

.ZpAuth__form {
  margin-top: var(--zp-space-lg);
}

.ZpAuth__error {
  margin-bottom: var(--zp-space-md) !important;
  font-size: var(--zp-text-sm);
}

.ZpAuth__hint {
  font-size: var(--zp-text-sm);
  color: var(--zp-text-secondary);
  margin: 0 0 var(--zp-space-md);
  line-height: var(--zp-leading-normal);
}

.ZpAuth__field {
  margin-bottom: var(--zp-space-2xs);

  .v-input__slot {
    border-radius: var(--zp-radius-md) !important;
    background: var(--zp-bg-elevated) !important;
  }

  .v-label {
    font-size: var(--zp-text-sm);
    color: var(--zp-text-secondary) !important;
  }

  // Lifted label (when field has focus or value) sits on the panel surface,
  // not on the field background. Without this override Vuetify lays it inside
  // the input, where it overlaps the value.
  .v-label--active {
    background: var(--zp-bg-surface);
    padding: 0 4px;
    transform: translateY(-18px) scale(0.78) !important;
    color: var(--zp-text-secondary) !important;
  }

  &.v-input--is-focused .v-label--active {
    color: var(--zp-brand) !important;
  }

  // Outlined fieldset border picks up token, so it stays visible in dark.
  fieldset {
    border-color: var(--zp-border-default) !important;
  }

  &.v-input--is-focused fieldset {
    border-color: var(--zp-brand) !important;
    border-width: 1.5px !important;
  }

  // The notch legend that holds the lifted label has its own background;
  // make sure it sits on the field background to avoid the "label-over-input"
  // look.
  legend {
    background: transparent;
  }

  input {
    color: var(--zp-text-primary) !important;
  }
}

.ZpAuth__btn {
  margin-top: var(--zp-space-sm);
  height: 44px !important;
  border-radius: var(--zp-radius-md) !important;
  font-weight: 600;
  letter-spacing: var(--zp-tracking-label);
  text-transform: none !important;
  font-size: var(--zp-text-base) !important;
}

.ZpAuth__btn--secondary {
  margin-top: var(--zp-space-xs);
}

.ZpAuth__divider {
  display: flex;
  align-items: center;
  gap: var(--zp-space-sm);
  margin: var(--zp-space-md) 0 var(--zp-space-sm);
  color: var(--zp-text-tertiary);
  font-size: var(--zp-text-xs);
  letter-spacing: var(--zp-tracking-overline);
  text-transform: uppercase;

  &::before,
  &::after {
    content: '';
    flex: 1 1 auto;
    height: 1px;
    background: var(--zp-border-hairline);
  }
}

.ZpAuth__row {
  display: flex;
  justify-content: space-between;
  gap: var(--zp-space-md);
  margin-top: var(--zp-space-md);
  font-size: var(--zp-text-sm);

  &--center {
    justify-content: center;
  }
}

.ZpAuth__link {
  color: var(--zp-brand);
  cursor: pointer;
  text-decoration: none;

  &:hover {
    color: var(--zp-brand-hover);
    text-decoration: underline;
  }
}

.ZpAuth__footer {
  margin-top: var(--zp-space-xl);
  padding-top: var(--zp-space-md);
  border-top: var(--zp-border-hair) solid var(--zp-border-hairline);
  text-align: center;
  font-size: var(--zp-text-2xs);
  letter-spacing: var(--zp-tracking-overline);
  text-transform: uppercase;
  color: var(--zp-text-tertiary);
  font-weight: 600;
}

// Dark theme tuning
.v-application.theme--dark .ZpAuth {
  background:
    radial-gradient(
      circle at 80% -10%,
      color-mix(in oklch, var(--zp-brand) 22%, transparent) 0%,
      transparent 45%
    ),
    radial-gradient(
      circle at -10% 110%,
      color-mix(in oklch, var(--zp-brand) 14%, transparent) 0%,
      transparent 50%
    ),
    var(--zp-bg-page);
}

.v-application.theme--dark .ZpAuth__panel {
  box-shadow:
    0 1px 2px rgba(0, 0, 0, 0.4),
    0 12px 28px -8px rgba(0, 0, 0, 0.5);
}
</style>

<script>
import axios from 'axios';
import { getErrorMessage } from '@/lib/error';
import EventBus from '@/event-bus';

export default {
  data() {
    return {
      signInFormValid: false,
      signInError: null,
      signInProcess: false,

      password: null,
      username: null,

      email: null,

      oidcProviders: [],
      loginWithPassword: null,
      authMethods: {},

      screen: 'sign-in',

      verificationCode: null,
      verificationMethod: null,
      recoveryCode: null,
      verificationEmailSending: false,
    };
  },

  async created() {
    const { status, verificationMethod } = await this.getAuthenticationStatus();

    switch (status) {
      case 'authenticated':
        this.redirectAfterLogin();
        break;
      case 'unauthenticated':
        await this.loadLoginData();
        break;
      case 'unverified':
        this.screen = 'verification';
        this.verificationMethod = verificationMethod;
        await this.loadLoginData();
        break;
      default:
        throw new Error(`Unknown authentication status: ${status}`);
    }
  },

  computed: {
    isPortal() {
      return process.env.VUE_APP_BUILD_TYPE === 'pro_portal';
    },
  },

  methods: {
    async resendEmailVerification() {
      if (this.verificationEmailSending) {
        return;
      }

      this.verificationEmailSending = true;
      try {
        (await axios({
          method: 'post',
          url: '/api/auth/login/email/resend',
          responseType: 'json',
        }));
        EventBus.$emit('i-snackbar', {
          color: 'success',
          text: 'Verification email sent successfully.',
        });
      } catch (e) {
        EventBus.$emit('i-snackbar', {
          color: 'error',
          text: getErrorMessage(e),
        });
      } finally {
        this.verificationEmailSending = false;
      }
    },

    async loadLoginData() {
      await axios({
        method: 'get',
        url: '/api/auth/login',
        responseType: 'json',
      }).then((resp) => {
        this.oidcProviders = resp.data.oidc_providers;
        this.loginWithPassword = resp.data.login_with_password;
        this.authMethods = resp.data.auth_methods || {};
      });
    },

    async recovery() {
      this.signInProcess = true;

      try {
        await axios({
          method: 'post',
          url: '/api/auth/recovery',
          responseType: 'json',
          data: {
            recovery_code: this.recoveryCode,
          },
        });

        const { location } = document;
        document.location = location;
      } catch (e) {
        this.signInError = getErrorMessage(e);
      } finally {
        this.signInProcess = false;
      }
    },

    async signOut() {
      try {
        (await axios({
          method: 'post',
          url: '/api/auth/logout',
          responseType: 'json',
        }));

        const { location } = document;
        document.location = location;
      } catch (e) {
        EventBus.$emit('i-snackbar', {
          color: 'error',
          text: getErrorMessage(e),
        });
      }
    },

    async getAuthenticationStatus() {
      try {
        await axios({
          method: 'get',
          url: '/api/user',
          responseType: 'json',
        });
      } catch (err) {
        if (err.response.status === 401) {
          switch (err.response.data.error) {
            case 'TOTP_REQUIRED':
              return {
                status: 'unverified',
                verificationMethod: 'totp',
              };
            case 'EMAIL_OTP_REQUIRED':
              return {
                status: 'unverified',
                verificationMethod: 'email',
              };
            default:
              return { status: 'unauthenticated' };
          }
        }
        throw err;
      }

      return { status: 'authenticated' };
    },

    async verify() {
      this.signInError = null;

      if (!this.$refs.signInForm.validate()) {
        return;
      }

      this.signInProcess = true;

      try {
        await axios({
          method: 'post',
          url: '/api/auth/verify',
          responseType: 'json',
          data: {
            passcode: this.verificationCode,
          },
        });

        this.redirectAfterLogin();
      } catch (err) {
        this.signInError = getErrorMessage(err);
      } finally {
        this.signInProcess = false;
      }
    },

    async signInWithEmail() {
      this.signInError = null;

      if (!this.$refs.signInForm.validate()) {
        return;
      }

      this.signInProcess = true;
      try {
        await axios({
          method: 'post',
          url: '/api/auth/login/email',
          responseType: 'json',
          data: {
            email: this.email,
          },
        });

        this.redirectAfterLogin();
      } catch (err) {
        if (err.response.status === 401) {
          this.signInError = this.$t('incorrectEmail');
        } else {
          this.signInError = getErrorMessage(err);
        }
      } finally {
        this.signInProcess = false;
      }
    },

    async signIn() {
      this.signInError = null;

      if (!this.$refs.signInForm.validate()) {
        return;
      }

      this.signInProcess = true;
      try {
        await axios({
          method: 'post',
          url: '/api/auth/login',
          responseType: 'json',
          data: {
            auth: this.username,
            password: this.password,
          },
        });

        this.redirectAfterLogin();
      } catch (err) {
        if (err.response.status === 401) {
          this.signInError = this.$t('incorrectUsrPwd');
        } else {
          this.signInError = getErrorMessage(err);
        }
      } finally {
        this.signInProcess = false;
      }
    },

    async oidcSignIn(provider) {
      const params = new URLSearchParams();
      const returnTo = this.$route.query.return;
      if (returnTo) {
        params.set('return', returnTo);
      } else if (this.$route.query.new_project === 'premium') {
        params.set('return', '/project/premium');
      }
      const qs = params.toString();
      const suffix = qs ? `?${qs}` : '';
      document.location = `${document.baseURI}api/auth/oidc/${provider}/login${suffix}`;
    },

    redirectAfterLogin() {
      const redirectTo = this.$route.query.return;
      let baseURI = document.baseURI;

      if (redirectTo) {
        if (baseURI.endsWith('/')) {
          baseURI = baseURI.substring(0, baseURI.length - 1);
        }

        document.location = baseURI + redirectTo;

        return;
      }

      document.location = document.baseURI + window.location.search;
    },
  },
};
</script>
